import { Injectable } from '@angular/core';

export interface CacheItem {
  key: string;
  data: any;
  timestamp: number;
  expiry?: number;
}

@Injectable({
  providedIn: 'root'
})
export class IndexedDBCacheService {
  private dbName = 'CustomerOutputCache';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;

  constructor() {
    this.initDB();
  }

  public async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('Errore nell\'apertura del database IndexedDB');
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('Database IndexedDB inizializzato con successo');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Store per i dati degli endpoint REST
        if (!db.objectStoreNames.contains('restEndpoints')) {
          const restStore = db.createObjectStore('restEndpoints', { keyPath: 'key' });
          restStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Store per le immagini dinamiche
        if (!db.objectStoreNames.contains('dynamicImages')) {
          const imageStore = db.createObjectStore('dynamicImages', { keyPath: 'key' });
          imageStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Store per i dati delle pagine
        if (!db.objectStoreNames.contains('pageData')) {
          const pageStore = db.createObjectStore('pageData', { keyPath: 'key' });
          pageStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  async setItem(storeName: string, key: string, data: any, expiryMinutes?: number): Promise<void> {
    if (!this.db) {
      await this.initDB();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);

      const cacheItem: CacheItem = {
        key,
        data,
        timestamp: Date.now(),
        expiry: expiryMinutes ? Date.now() + (expiryMinutes * 60 * 1000) : undefined
      };

      const request = store.put(cacheItem);

      request.onsuccess = () => {
        console.log(`Dati salvati in cache: ${storeName}/${key}`);
        resolve();
      };

      request.onerror = () => {
        console.error(`Errore nel salvare i dati in cache: ${storeName}/${key}`);
        reject(request.error);
      };
    });
  }

  async getItem(storeName: string, key: string): Promise<any | null> {
    if (!this.db) {
      await this.initDB();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result as CacheItem;

        if (!result) {
          resolve(null);
          return;
        }

        // Controlla se il dato è scaduto
        if (result.expiry && Date.now() > result.expiry) {
          console.log(`Dato scaduto rimosso dalla cache: ${storeName}/${key}`);
          this.removeItem(storeName, key);
          resolve(null);
          return;
        }

        console.log(`Dati recuperati dalla cache: ${storeName}/${key}`);
        resolve(result.data);
      };

      request.onerror = () => {
        console.error(`Errore nel recuperare i dati dalla cache: ${storeName}/${key}`);
        reject(request.error);
      };
    });
  }

  async removeItem(storeName: string, key: string): Promise<void> {
    if (!this.db) {
      await this.initDB();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      request.onsuccess = () => {
        console.log(`Dato rimosso dalla cache: ${storeName}/${key}`);
        resolve();
      };

      request.onerror = () => {
        console.error(`Errore nella rimozione del dato dalla cache: ${storeName}/${key}`);
        reject(request.error);
      };
    });
  }

  async clearStore(storeName: string): Promise<void> {
    if (!this.db) {
      await this.initDB();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => {
        console.log(`Store pulito: ${storeName}`);
        resolve();
      };

      request.onerror = () => {
        console.error(`Errore nella pulizia dello store: ${storeName}`);
        reject(request.error);
      };
    });
  }

  async getAllKeys(storeName: string): Promise<string[]> {
    if (!this.db) {
      await this.initDB();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAllKeys();

      request.onsuccess = () => {
        resolve(request.result as string[]);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  // Metodo per pulire i dati scaduti
  async cleanExpiredData(): Promise<void> {
    const stores = ['restEndpoints', 'dynamicImages', 'pageData'];

    for (const storeName of stores) {
      try {
        const keys = await this.getAllKeys(storeName);

        for (const key of keys) {
          await this.getItem(storeName, key);
          // getItem già rimuove automaticamente i dati scaduti
        }
      } catch (error) {
        console.error(`Errore nella pulizia dei dati scaduti per ${storeName}:`, error);
      }
    }
  }
}